import { useTranslation } from "react-i18next";
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router";

import { cellarIdOf, CellarProvider } from "./cellar.js";
import { CacheNotice } from "./components/CacheNotice.js";
import { CellarBackdrop } from "./components/CellarBackdrop.js";
import { LanguageSwitch } from "./components/LanguageSwitch.js";
import { Mark } from "./components/Mark.js";
import { AreaReportPage } from "./screens/AreaReportPage.js";
import { AreaReports } from "./screens/AreaReports.js";
import { Cellar } from "./screens/Cellar.js";
import { Discover } from "./screens/Discover.js";
import { Cellars } from "./screens/Cellars.js";
import { Export } from "./screens/Export.js";
import { Gaps } from "./screens/Gaps.js";
import { Drunk } from "./screens/Drunk.js";
import { Notes } from "./screens/Notes.js";
import { Wishes } from "./screens/Wishes.js";
import { Guests } from "./screens/Guests.js";
import { Meal } from "./screens/Meal.js";
import { Quality } from "./screens/Quality.js";
import { WhatsNew } from "./components/WhatsNew.js";
import { Settings } from "./screens/Settings.js";
import { SignIn } from "./screens/SignIn.js";
import { Stocktake } from "./screens/Stocktake.js";
import { ToOpen } from "./screens/ToOpen.js";
import { ToServe } from "./screens/ToServe.js";
import { Transfer } from "./screens/Transfer.js";
import { WindowPreview } from "./screens/WindowPreview.js";
import { Producers } from "./screens/Producers.js";
import { Statistics } from "./screens/Statistics.js";
import { Vocabulary } from "./screens/Vocabulary.js";
import { Wine } from "./screens/Wine.js";
import { WineEditor } from "./screens/WineEditor.js";
import { Wines } from "./screens/Wines.js";
import { useSession } from "./session.js";
import { version } from "./version.js";

/** A cog in the line weight of the list's own arrows, taking its colour from
 *  the muted text it stands among. */
function Cog() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true" className="h-4 w-4">
      <g
        fill="none"
        stroke="currentColor"
        strokeWidth="1.75"
        strokeLinecap="round"
        strokeLinejoin="round"
      >
        <circle cx="12" cy="12" r="3" />
        <path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 1 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 1 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 1 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 1 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 1 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 1 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 1 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 1 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z" />
      </g>
    </svg>
  );
}

function Header() {
  const { t } = useTranslation();
  const session = useSession();

  // Centred rather than on a baseline: the mark has no baseline of its own,
  // and a flex row that tries to find one in an SVG hangs the name off the
  // bottle's foot.
  return (
    <header className="border-line flex items-center justify-between border-b px-5 py-4">
      {/* The name is the way back to the start. Styled as the interface's
          quiet interactive colour rather than as a button, because it is a
          way home and not an action. */}
      <Link
        to="/"
        className="font-display hover:text-accent group flex shrink-0 items-center gap-2.5 text-lg tracking-tight"
      >
        {/* The mark sits on the text's baseline-ish centre and does not move
            on hover; the underline is the affordance, the bottle is not a
            button. `self-center` rather than a baseline, because a bottle
            aligned by its foot reads as falling over. */}
        <Mark className="h-6 w-auto shrink-0 self-center" />
        <span className="underline-offset-4 group-hover:underline">{t("app.name")}</span>
      </Link>
      {/* The language is chosen here whether or not anybody is signed in,
          because the sign-in page is read before there is an account. */}
      {session.status !== "signedIn" && <LanguageSwitch />}
      {/* The account's corner: the cog, signing out, the language. Until
          2026-10-07 it also held *Upptäck* and the owner's name, and on a phone
          the five did not fit — the name and *Logga ut* each broke onto two
          lines. *Upptäck* and the name went under a cellar's *Mer*, where the
          decision in `project-scope.md` puts them. Nothing here wraps, and it
          is centred on one line, because a cog and a flag have no baseline for
          words to sit on. */}
      {session.status === "signedIn" && (
        <div className="text-muted flex items-center gap-4 text-sm whitespace-nowrap sm:gap-5">
          {/* The administrator's switches, since 2026-09-28. Here rather than
              in a cellar because they reach every cellar at once, and beside
              signing out because both are about the account.

              A cog rather than the word, on the owner's asking: the corner
              already carries a name and a verb, and a third word made it read
              as a sentence. The word stays as the link's name, for a screen
              reader and as the tooltip. Kept beside the entry *Mer* has had
              since 2026-10-08, because the list of cellars has no *Mer*. The
              page used to be handed the address the cog was pressed on, for a
              *Tillbaka* that the menu replaced the same day. */}
          {session.user.isAdministrator && (
            <Link
              to="/installningar"
              aria-label={t("settings.title")}
              title={t("settings.title")}
              className="hover:text-accent"
            >
              <Cog />
            </Link>
          )}
          <button
            type="button"
            onClick={() => void session.signOut()}
            className="hover:text-accent underline-offset-4 hover:underline"
          >
            {t("nav.signOut")}
          </button>
          <LanguageSwitch />
        </div>
      )}
    </header>
  );
}

/**
 * The version, and nothing else. It is the one piece of chrome that is for the
 * person maintaining this rather than for the person using it — *is the phone
 * showing what I just deployed?* — so it is as quiet as a thing on the page
 * can be: the muted ink, the smallest size, and the last thing on the page
 * rather than something pinned over the last row of a list.
 */
function Footer() {
  const { t } = useTranslation();

  return (
    // `w-full` is load-bearing, as it is on <main>: an auto margin across a
    // flex column's cross axis turns off the stretch, so without it the footer
    // shrinks to fit its own text and the version lands in the middle of the
    // page rather than at the right-hand end of the measure.
    <footer className="mx-auto w-full max-w-3xl px-5 pb-8 text-right">
      <span className="text-muted text-xs">{t("app.version", { version })}</span>
    </footer>
  );
}

/**
 * Where `/kallare/:id/viner` used to go. The wine list is now the cellar
 * itself, and the search that was in the query string comes along — a
 * bookmarked filtered list should still open filtered.
 */
function WinesMoved() {
  const { cellarId = "" } = useParams();
  const location = useLocation();
  return <Navigate to={`/kallare/${cellarId}${location.search}`} replace />;
}

export function App() {
  const { t } = useTranslation();
  const session = useSession();
  // Read from the address rather than from a route parameter, which is only
  // available below `<Routes>`. That placement is the point: the provider then
  // does not remount as you move between the sections of one cellar, so the
  // role is fetched once on the way in rather than once per page.
  const cellarId = cellarIdOf(useLocation().pathname);

  return (
    // The ground colour stays on <body> and is not repeated here, so the
    // backdrop behind this element is something to see through to rather than
    // something painted over.
    //
    // A column that fills the window, so the foot of the page is the foot of
    // the window on a short screen — the sign-in page would otherwise hang its
    // version line under three fields with the rest of the glass empty below
    // it. The backdrop is `fixed` and takes no part in this.
    <div className="text-ink flex min-h-dvh flex-col">
      <CellarBackdrop />
      <Header />
      <CacheNotice />
      {/* These two numbers are also `--ck-measure` and `--ck-page-gutter` in
          styles.css, which is how CellarBackdrop lines its cluster up with the
          right-hand end of the rules below. Change the measure here and change
          it there. */}
      <main className="mx-auto w-full max-w-3xl grow px-5 py-10">
        {/* "unknown" is not "signed out": treating the unanswered question as a
            negative flashes the sign-in screen at somebody who is signed in,
            on every reload. */}
        {session.status === "unknown" ? (
          <p className="text-muted text-sm">{t("loading")}</p>
        ) : (
          <CellarProvider cellarId={cellarId}>
            <WhatsNew />
            <Routes>
              <Route
                path="/"
                element={
                  session.status === "signedIn" ? (
                    <Cellars />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* A cellar opens on its wines. The cabinets and shelves are
                furniture, set up once and corrected rarely, so they live on a
                page of their own rather than in front of the list. */}
              <Route
                path="/kallare/:cellarId"
                element={
                  session.status === "signedIn" ? (
                    <Wines />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/skap"
                element={
                  session.status === "signedIn" ? (
                    <Cellar />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* What to open, and why this bottle rather than another. A static
                segment above the wine routes, like the two below it. */}
              <Route
                path="/kallare/:cellarId/oppna"
                element={
                  session.status === "signedIn" ? (
                    <ToOpen />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Which bottle suits what is being cooked. A section of its own
                since 2026-09-18 — it was built onto *Att öppna*, where its
                answer was drawn in the same rows as the wines that are running
                out, so two lists said opposite things in one shape. */}
              <Route
                path="/kallare/:cellarId/mat"
                element={
                  session.status === "signedIn" ? (
                    <Meal />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* What to carry from the ageing cabinet to the fridge. Reached
                from *Att öppna* rather than from the row of sections, because
                the prompt belongs where the question is asked. */}
              <Route
                path="/kallare/:cellarId/servering"
                element={
                  session.status === "signedIn" ? (
                    <ToServe />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Walking a shelf and confirming what is on it. A static segment
                above the wine routes, like the others. */}
              <Route
                path="/kallare/:cellarId/inventering"
                element={
                  session.status === "signedIn" ? (
                    <Stocktake />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* The walk through the cabinets, filling in what the cellar does
                not know about its own bottles. Above the wine routes for the
                same reason `nytt` is: a static segment is not a wine. */}
              <Route
                path="/kallare/:cellarId/luckor"
                element={
                  session.status === "signedIn" ? (
                    <Gaps />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* What everybody has written in this cellar, and which of it is
                new to the reader. Reached from the line above the wine list
                rather than from the row of sections — see `CellarHeading`. */}
              <Route
                path="/kallare/:cellarId/anteckningar"
                element={
                  session.status === "signedIn" ? (
                    <Notes />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/drucket"
                element={
                  session.status === "signedIn" ? (
                    <Drunk />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* The reader's own wishes for wines not yet owned. */}
              <Route
                path="/kallare/:cellarId/onskelista"
                element={
                  session.status === "signedIn" ? (
                    <Wishes />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route path="/kallare/:cellarId/viner" element={<WinesMoved />} />
              {/* Registering and correcting a wine are the same ten fields, so
                they are the same screen. The static segments outrank
                `:wineId`, so `nytt` is the form and not a wine. */}
              <Route
                path="/kallare/:cellarId/viner/nytt"
                element={
                  session.status === "signedIn" ? (
                    <WineEditor />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/viner/:wineId/andra"
                element={
                  session.status === "signedIn" ? (
                    <WineEditor />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/viner/:wineId"
                element={
                  session.status === "signedIn" ? (
                    <Wine />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/producenter"
                element={
                  session.status === "signedIn" ? (
                    <Producers />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* The quality report and its history. Reached from Producenter,
                because half of what it proposes is a producer's standing. */}
              <Route
                path="/kallare/:cellarId/kvalitet"
                element={
                  session.status === "signedIn" ? (
                    <Quality />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/statistik"
                element={
                  session.status === "signedIn" ? (
                    <Statistics />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* The words this cellar has decided, and correcting one. Reached
                from Producenter rather than from the row of sections. */}
              <Route
                path="/kallare/:cellarId/ord"
                element={
                  session.status === "signedIn" ? (
                    <Vocabulary />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Who else may read this cellar. Beside the statistics and the
                export, because all three are acts of ownership over the whole
                cellar rather than something done to a wine. */}
              <Route
                path="/kallare/:cellarId/gaster"
                element={
                  session.status === "signedIn" ? (
                    <Guests />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Handing the cellar over. Reached from *Gäster* rather than
                from the row of sections, which already wraps onto three lines
                on a phone — and this is done once in the life of a cellar, if
                ever. */}
              <Route
                path="/kallare/:cellarId/overlata"
                element={
                  session.status === "signedIn" ? (
                    <Transfer />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/kallare/:cellarId/export"
                element={
                  session.status === "signedIn" ? (
                    <Export />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/installningar"
                element={
                  session.status === "signedIn" ? (
                    <Settings />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Every area report there has been, and running one — the
                administrator's, reached from Inställningar. */}
              <Route
                path="/installningar/omraden"
                element={
                  session.status === "signedIn" ? (
                    <AreaReports />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Descriptions of wine areas, for every account alike. Not
                under a cellar, because a report belongs to none. */}
              <Route
                path="/upptack"
                element={
                  session.status === "signedIn" ? (
                    <Discover />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              <Route
                path="/upptack/:reportId"
                element={
                  session.status === "signedIn" ? (
                    <AreaReportPage />
                  ) : (
                    <Navigate to="/logga-in" replace />
                  )
                }
              />
              {/* Development only: the band has nothing real to draw until
                phase 3, and this is where it is looked at meanwhile. */}
              {import.meta.env.DEV && (
                <Route path="/fonster" element={<WindowPreview />} />
              )}
              <Route
                path="/logga-in"
                element={
                  session.status === "signedIn" ? <Navigate to="/" replace /> : <SignIn />
                }
              />
            </Routes>
          </CellarProvider>
        )}
      </main>
      <Footer />
    </div>
  );
}
